import { useEffect, useState, useCallback } from 'react';
import {
  useCoupons,
  useCoupon,
  useDeleteCoupon,
  useCouponPdf,
  useUpdateCoupon,
  useRegenerateCouponQrs,
} from '../../../hooks/useCoupons';
import { usePlans } from '../../../hooks/usePlans';
import { useGroups } from '../../../hooks/useGroups';
import { calculateInterest, transformCouponsData } from '../utils/couponsUtils';
import { ITEMS_PER_PAGE } from '../constants/couponsConstants';

const initialEditCoupon = {
  contrato: '',
  plan: '',
  cuotas: '',
  first_interest: 0,
  second_interest: 0,
  third_interest: 0,
  items: {
    pei: '',
    social_fee: '',
    life_insurance: '',
    fire_insurance: '',
    property_tax: '',
    uuid_pei: '',
    uuid_social_fee: '',
    uuid_fire_insurance: '',
    uuid_life_insurance: '',
    uuid_property_tax: '',
  },
  fee_mount: 0,
};

export const useCouponsTable = () => {
  const { data: plans = [] } = usePlans();
  const { data: groups = [] } = useGroups();

  const [selectedCouponId, setSelectedCouponId] = useState(null);
  const [data, setData] = useState([]);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(ITEMS_PER_PAGE);
  const [filter, setFilter] = useState({
    quarter: '',
    year: new Date().getFullYear(),
    plan: plans.length > 0 ? plans[0]?.uuid : '',
    group: '',
    contract_number: '',
  });
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [filters, setFilters] = useState({
    quarter: '',
    year: new Date().getFullYear(),
    plan: plans.length > 0 ? plans[0]?.uuid : '',
    group: '',
    contract_number: '',
    page: page,
    per_page: ITEMS_PER_PAGE,
  });
  const [downloadingPdfId, setDownloadingPdfId] = useState(null);
  const [currentRow, setCurrentRow] = useState(null);
  const [coeficienteActual, setCoeficienteActual] = useState(0);
  const [open, setOpen] = useState(false);
  const [openAdelantoDialog, setOpenAdelantoDialog] = useState(false);
  const [selectedData, setSelectedData] = useState(initialEditCoupon);
  const [regeneratingId, setRegeneratingId] = useState(null);
  const [shouldOpenDialog, setShouldOpenDialog] = useState(false);

  const { data: couponsResponse, isLoading } = useCoupons(filters);
  const deleteMutation = useDeleteCoupon();
  const updateMutation = useUpdateCoupon();
  const regenerateMutation = useRegenerateCouponQrs();
  const { data: pdfData } = useCouponPdf(downloadingPdfId, {
    enabled: !!downloadingPdfId,
  });
  const { data: couponData } = useCoupon(selectedCouponId, {
    enabled: !!selectedCouponId,
  });

  useEffect(() => {
    if (pdfData && downloadingPdfId) {
      try {
        let pdfBytes = atob(pdfData.base64_pdf);
        let bytes = new Uint8Array(pdfBytes.length);
        for (let i = 0; i < pdfBytes.length; i++) {
          bytes[i] = pdfBytes.charCodeAt(i);
        }
        let blob = new Blob([bytes.buffer], { type: 'application/pdf' });
        let url = URL.createObjectURL(blob);

        const fileName = pdfData.file_name || 'cupon.pdf';

        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        window.open(url);

        setTimeout(() => URL.revokeObjectURL(url), 100);
      } catch (error) {
        console.error('Error downloading PDF:', error);
      } finally {
        setDownloadingPdfId(null);
      }
    }
  }, [pdfData, downloadingPdfId]);

  useEffect(() => {
    if (couponData && currentRow) {
      const plan = plans.find((plan) => plan.uuid === couponData.uuid_plan);

      const toWhole = (value) => {
        const numberValue = typeof value === 'number' ? value : parseFloat(value);
        return Number.isFinite(numberValue) ? Math.round(numberValue) : 0;
      };
      const formatWhole = (value) => toWhole(value).toString();

      const buildInterest = (baseAmount, baseDate, targetDate) => {
        const interestData = calculateInterest(baseAmount, baseDate, targetDate);
        return {
          interest: formatWhole(interestData.interest),
          total: formatWhole(interestData.total),
        };
      };

      const items = couponData.items || {};
      const formattedItems = Object.entries(items).reduce((acc, [key, value]) => {
        acc[key] = key.startsWith('uuid_') ? value : formatWhole(value);
        return acc;
      }, {});

      const baseFee =
        toWhole(couponData.first_due_amount) -
        toWhole(items?.fire_insurance) -
        toWhole(items?.life_insurance) -
        toWhole(items?.pei) -
        toWhole(items?.property_tax) -
        toWhole(items?.social_fee);

      const secondInterest = buildInterest(
        couponData.first_due_amount,
        couponData.first_due_date,
        couponData.second_due_date
      );
      const thirdInterest = buildInterest(
        couponData.first_due_amount,
        couponData.first_due_date,
        couponData.third_due_date
      );

      const finalData = {
        ...couponData,
        orden: currentRow.orden,
        cuotas: currentRow.cuotas,
        plan,
        first_interest: formatWhole(couponData.first_interest ?? 0),
        first_due_amount: formatWhole(couponData.first_due_amount),
        second_interest: secondInterest.interest,
        third_interest: thirdInterest.interest,
        second_due_amount: secondInterest.total,
        third_due_amount: thirdInterest.total,
        fee_mount: formatWhole(baseFee),
        items: formattedItems,
      };

      setSelectedData(finalData);
      setCoeficienteActual(finalData.score_coefficent);

      if (shouldOpenDialog) {
        if (couponData.fee_type_name === 'Adelanto') {
          setOpenAdelantoDialog(true);
        } else {
          setOpen(true);
        }
        setShouldOpenDialog(false);
      }
    }
  }, [couponData, currentRow, plans, calculateInterest, shouldOpenDialog]);

  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      page,
      per_page: rowsPerPage,
    }));
  }, [page, rowsPerPage]);

  useEffect(() => {
    if (couponsResponse) {
      const transformedCoupons = transformCouponsData(couponsResponse.coupons);
      setData(transformedCoupons);
      setTotalPages(couponsResponse.pages);
      setTotalCount(couponsResponse.total);
    }
  }, [couponsResponse]);

  const setEditCouponField = useCallback(({ key, value, nestedKey }) => {
    setSelectedData((prev) => {
      if (nestedKey) {
        return {
          ...prev,
          [nestedKey]: {
            ...prev[nestedKey],
            [key]: value,
          },
        };
      } else {
        return {
          ...prev,
          [key]: value,
        };
      }
    });
  }, []);

  const handleDownload = useCallback((uuid) => {
    setDownloadingPdfId(uuid);
  }, []);

  const handleFilter = useCallback(
    (event) => {
      const newFilter = {
        ...filter,
        [event.target.name]: event.target.value,
      };
      setFilter(newFilter);
      setFilters({
        ...newFilter,
        page: 1,
        per_page: rowsPerPage,
      });
      setPage(1);
    },
    [filter, rowsPerPage]
  );

  const handleOpenDialog = useCallback(async (uuid, row) => {
    setCurrentRow(row);
    setSelectedCouponId(uuid);
    setShouldOpenDialog(true);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
    setOpenAdelantoDialog(false);
    setSelectedData(initialEditCoupon);
    setSelectedCouponId(null);
    setCurrentRow(null);
    setShouldOpenDialog(false);
  }, []);

  const handleSave = useCallback(async () => {
    const toWhole = (value) => {
      const parsedValue = parseFloat(value);
      return Number.isFinite(parsedValue) ? Math.round(parsedValue) : 0;
    };

    const {
      uuid,
      first_due_amount,
      second_due_amount,
      third_due_amount,
      first_interest,
      second_interest,
      third_interest,
      fee_mount,
      score_coefficent,
      items: {
        pei,
        social_fee,
        fire_insurance,
        life_insurance,
        property_tax,
        uuid_pei,
        uuid_social_fee,
        uuid_fire_insurance,
        uuid_life_insurance,
        uuid_property_tax,
      },
      first_due_date,
      second_due_date,
      third_due_date,
    } = selectedData;

    const data = {
      first_due_amount: toWhole(first_due_amount),
      second_due_amount: toWhole(second_due_amount),
      third_due_amount: toWhole(third_due_amount),
      first_interest: toWhole(first_interest),
      second_interest: toWhole(second_interest),
      third_interest: toWhole(third_interest),
      fee_mount: toWhole(fee_mount),
      score_coefficent: parseFloat(score_coefficent),
      items: {
        pei: toWhole(pei),
        social_fee: toWhole(social_fee),
        fire_insurance: toWhole(fire_insurance),
        life_insurance: toWhole(life_insurance),
        property_tax: toWhole(property_tax),
        uuid_pei,
        uuid_social_fee,
        uuid_fire_insurance,
        uuid_life_insurance,
        uuid_property_tax,
      },
      first_due_date,
      second_due_date,
      third_due_date,
    };

    try {
      await updateMutation.mutateAsync({ id: uuid, data });
      setOpenAdelantoDialog(false);
      setOpen(false);
      setSelectedData(initialEditCoupon);
      setShouldOpenDialog(false);
    } catch (error) {
      console.error('Error updating coupon:', error);
    }
  }, [selectedData, updateMutation]);

  const handleDeleteCoupon = useCallback(
    async (uuid) => {
      try {
        await deleteMutation.mutateAsync(uuid);
      } catch (error) {
        console.error(error);
      }
    },
    [deleteMutation]
  );

  const handleRegenerateCouponQrs = useCallback(
    async (uuid) => {
      try {
        setRegeneratingId(uuid);
        await regenerateMutation.mutateAsync(uuid);
      } catch (error) {
        console.error('Error regenerating coupon QRs:', error);
      } finally {
        setRegeneratingId(null);
      }
    },
    [regenerateMutation]
  );

  const handlePageChange = useCallback((event, value) => {
    setPage(value);
  }, []);

  const handleRowsPerPageChange = useCallback((event) => {
    const value = parseInt(event.target.value, 10);
    if (!Number.isNaN(value)) {
      setRowsPerPage(value);
      setPage(1);
      setFilters((prev) => ({
        ...prev,
        page: 1,
        per_page: value,
      }));
    }
  }, []);

  return {
    data,
    isLoading,
    page,
    totalPages,
    totalCount,
    rowsPerPage,
    filter,
    plans,
    groups,
    selectedData,
    coeficienteActual,
    open,
    openAdelantoDialog,
    handleDownload,
    handleFilter,
    handleOpenDialog,
    handleClose,
    handleSave,
    handleDeleteCoupon,
    handleRegenerateCouponQrs,
    regeneratingId,
    handlePageChange,
    handleRowsPerPageChange,
    setEditCouponField,
    calculateInterest,
  };
};
